import { AvailableDevices, SelectedDevices } from '../types/Video';
import { getDefaultDevice } from './getDefaultDevice';
import { getMediaDevices } from './getMediaDevices';
import { useEffect, useState } from 'react';

type ExtendedPermissionName = PermissionName | 'microphone' | 'camera';

const useMediaDevices = () => {
  const [availableDevices, setAvailableDevices] = useState<AvailableDevices>({
    audioInputs: [],
    audioOutputs: [],
    videoInputs: [],
  });
  const [selectedDevices, setSelectedDevices] = useState<SelectedDevices>({
    audioInput: null,
    audioOutput: null,
    videoInput: null,
  });

  const refreshDevices = async () => {
    const devices = await getMediaDevices();
    setAvailableDevices(devices);
    setSelectedDevices({
      audioInput: getDefaultDevice(devices.audioInputs),
      audioOutput: getDefaultDevice(devices.audioOutputs),
      videoInput: getDefaultDevice(devices.videoInputs),
    });
  };

  useEffect(() => {
    refreshDevices();

    const handleDeviceChange = () => {
      console.log('Device change detected');
      refreshDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    // Device labels are only exposed once permission is granted, so re-enumerate then.
    const permissionStatuses: PermissionStatus[] = [];
    const monitorPermissions = async () => {
      try {
        for (const name of ['microphone', 'camera'] as ExtendedPermissionName[]) {
          const status = await navigator.permissions.query({ name } as PermissionDescriptor);
          status.onchange = () => {
            console.log(`${name} permission changed:`, status.state);
            if (status.state === 'granted') refreshDevices();
          };
          permissionStatuses.push(status);
        }
      } catch (error) {
        console.error('Error monitoring permissions:', error);
      }
    };
    monitorPermissions();

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      permissionStatuses.forEach((status) => {
        status.onchange = null;
      });
    };
  }, []);

  return { availableDevices, selectedDevices, setSelectedDevices };
};

export default useMediaDevices;
